import Framebus from "framebus";

import {
  SipToken,
  SipAccount,
  SipNetwork,
  SipSignRes,
  RouteLocationRaw
} from "./types";
import { promiseWithTimeout } from './utils'
import {
  PlatformService,
  PlatformBaseService,
  PlatformId,
  FRAME_MESSAGE_TYPE,
  ServiceOptions,
} from "./base";

const MESSAGE_NAMESPACE = "SIP_EXT_FRAME";

const bus = new Framebus({
  channel: MESSAGE_NAMESPACE,
});

export class ExtensionIframeService implements PlatformBaseService {
  platformId = PlatformId.ExtensionIframe;
  options: ServiceOptions;

  constructor(options?: ServiceOptions) {
    this.options = { ...options }
  }

  async heartbeat(count: number, needStat?: boolean): ReturnType<PlatformService['heartbeat']> {
    const res = await promiseWithTimeout(bus.emitAsPromise<any>(
      FRAME_MESSAGE_TYPE.HEARTBEAT,
      { count, needStat }
    ), 'heartbeat timeout');

    return res;
  }

  async getVersion(): ReturnType<PlatformService["getVersion"]> {
    const res = await bus.emitAsPromise<any>(
      FRAME_MESSAGE_TYPE.GET_VERSION,
      {}
    );
    return res;
  }

  async getAccount(): Promise<SipAccount> {
    const account = await promiseWithTimeout(bus.emitAsPromise<SipAccount>(
      FRAME_MESSAGE_TYPE.GET_ACCOUNT,
      {}
    ), 'getAccount timeout');
    return account;
  }

  async getLocale(): ReturnType<PlatformService["getLocale"]> {
    const res = await bus.emitAsPromise<any>(FRAME_MESSAGE_TYPE.GET_LOCALE, {});
    return res;
  }

  async getFavoriteList(): ReturnType<PlatformService["getFavoriteList"]> {
    const res = await bus.emitAsPromise<any>(FRAME_MESSAGE_TYPE.GET_FAVORITE_LIST, {});
    return res;
  }

  async getRoute(): ReturnType<PlatformService["getRoute"]> {
    const res = await bus.emitAsPromise<any>(FRAME_MESSAGE_TYPE.GET_ROUTE, {});
    return res;
  }

  listenLocale(
    callback: (locale: string) => void
  ): ReturnType<PlatformService["listenLocale"]> {
    const handler = (data: any, relayHandler: any) => {
      callback(data.locale);
    };
    bus.on(FRAME_MESSAGE_TYPE.LISTEN_LOCALE, handler);

    return () => {
      bus.off(FRAME_MESSAGE_TYPE.LISTEN_LOCALE, handler);
    };
  }

  listenRouterPush(
    callback: (to: RouteLocationRaw) => void
  ): ReturnType<PlatformService["listenLocale"]> {
    const handler = (data: any, relayHandler: any) => {
      callback(data.to);
    };
    bus.on(FRAME_MESSAGE_TYPE.LISTEN_ROUTER_PUSH, handler);

    return () => {
      bus.off(FRAME_MESSAGE_TYPE.LISTEN_ROUTER_PUSH, handler);
    };
  }

  async getNetwork(): Promise<SipNetwork> {
    const res = await bus.emitAsPromise<SipNetwork>(
      FRAME_MESSAGE_TYPE.GET_NETWORK,
      {}
    );
    return res;
  }

  async getTokenMap(): ReturnType<PlatformBaseService["getTokenMap"]> {
    const res = await bus.emitAsPromise<any>(
      FRAME_MESSAGE_TYPE.GET_TOKEN_MAP,
      {}
    );
    return res;
  }

  // @TODO: 这部分后续应该从 localStorage 中读取，如果 parent 需要用到的话在写具体 Method
  async getStorage(): ReturnType<PlatformBaseService["getStorage"]> {
    const res = await bus.emitAsPromise<any>(
      FRAME_MESSAGE_TYPE.GET_STORAGE,
      {}
    );
    return res;
  }

  async signMessage(
    message: string
  ): ReturnType<PlatformService["signMessage"]> {
    const { signature, error } = await promiseWithTimeout(bus.emitAsPromise<SipSignRes>(
      FRAME_MESSAGE_TYPE.SIGN_MESSAGE,
      { message }
    ), 'Sign message timeout');

    if (error) {
      throw new Error(error)
    }

    if (!signature) {
      throw new Error("Signature is empty, please try again");
    }

    return signature;
  }

  async visitToken(token: SipToken): Promise<void> {
    await bus.emitAsPromise<void>(FRAME_MESSAGE_TYPE.VISIT_TOKEN, {
      tokenJson: JSON.stringify(token),
    });
  }

  async switchFavorite(token: SipToken): Promise<void> {
    await bus.emitAsPromise<void>(FRAME_MESSAGE_TYPE.SWITCH_FAVORITE, {
      tokenJson: JSON.stringify(token),
    });
  }

  async sendToBackgroundFromAction(message: string): Promise<any> {
    const res = await bus.emitAsPromise<any>(FRAME_MESSAGE_TYPE.SEND_TO_BACKGROUND_FROM_ACTION, {
      message,
    });
    return res;
  }

}
